import React, { useState, useRef } from 'react';
import { getApiBaseUrl } from '../designer/config/api';
import './DatabaseManager.css';

const DatabaseManager = ({ userId, onClose, onSelect }) => {
    const [step, setStep] = useState('select');
    const [file, setFile] = useState(null);
    const [fileInfo, setFileInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [datasets, setDatasets] = useState([]);
    const [datasetName, setDatasetName] = useState('');
    const [selectedSheet, setSelectedSheet] = useState('');
    const [manualData, setManualData] = useState([{ nombre: '', email: '', telefono: '' }]);
    const fileInputRef = useRef(null);

    // Estados para configuración de campos
    const [fieldConfigs, setFieldConfigs] = useState({});
    const [currentConfigDataset, setCurrentConfigDataset] = useState(null);
    const [datasetColumns, setDatasetColumns] = useState([]);

    // Estados para visualización de registros (Paginación)
    const [viewingDataset, setViewingDataset] = useState(null);
    const [viewRecords, setViewRecords] = useState([]);
    const [viewPage, setViewPage] = useState(1);
    const [viewLimit, setViewLimit] = useState(10);
    const [viewTotal, setViewTotal] = useState(0);
    const [viewColumns, setViewColumns] = useState([]);

    // Cargar configuraciones guardadas al iniciar
    React.useEffect(() => {
        try {
            const saved = localStorage.getItem('matic_dataset_field_configs');
            if (saved) setFieldConfigs(JSON.parse(saved));
        } catch (e) {
            console.error('Error loading field configs', e);
        }
    }, []);

    const saveFieldConfig = () => {
        localStorage.setItem('matic_dataset_field_configs', JSON.stringify(fieldConfigs));
        alert('Configuración guardada exitosamente');
        setStep('view');
    };

    const updateFieldConfig = (datasetId, field, key, value) => {
        setFieldConfigs(prev => ({
            ...prev,
            [datasetId]: {
                ...prev[datasetId],
                [field]: {
                    ...prev[datasetId]?.[field],
                    [key]: value
                }
            }
        }));
    };

    const handleConfigureDataset = async (dataset) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/records?user_id=${userId}&dataset_name=${encodeURIComponent(dataset.dataset_name)}`);
            const data = await response.json();

            if (data.success && data.records && data.records.length > 0) {
                const firstRecord = data.records[0];
                const recordData = firstRecord.data || firstRecord;
                const columns = Object.keys(recordData).filter(k =>
                    !['_id', 'dataset_id', 'import_id', '__v', 'created_at', 'updated_at'].includes(k)
                );

                setDatasetColumns(columns);
                setCurrentConfigDataset(dataset);
                setStep('configure_fields');
            } else {
                alert('No se pudieron obtener los campos del dataset o está vacío');
            }
        } catch (error) {
            console.error('Error fetching records for config:', error);
            alert('Error cargando campos del dataset');
        } finally {
            setLoading(false);
        }
    };

    // Función para ver registros paginados
    const handleViewRecords = async (dataset) => {
        setViewingDataset(dataset);
        setViewPage(1);
        // Resetear límite a 10 al abrir nuevo dataset
        setViewLimit(10);
        await fetchDatasetRecords(dataset, 1, 10);
        setStep('view_records');
    };

    const fetchDatasetRecords = async (dataset, page, limit) => {
        setLoading(true);
        try {
            const skip = (page - 1) * limit;
            const response = await fetch(`${API_URL}/records?user_id=${userId}&dataset_name=${encodeURIComponent(dataset.dataset_name)}&limit=${limit}&skip=${skip}`);
            const data = await response.json();

            if (data.success) {
                setViewRecords(data.records);
                // Si la API devuelve el total, usarlo. Si no, usar el del dataset object.
                // Asumimos que data.total podría venir en el futuro, por ahora usamos dataset.record_count
                setViewTotal(dataset.record_count || 0);

                if (data.records.length > 0) {
                    const firstRecord = data.records[0];
                    const recordData = firstRecord.data || firstRecord;
                    const columns = Object.keys(recordData).filter(k =>
                        !['_id', 'dataset_id', 'import_id', '__v', 'created_at', 'updated_at'].includes(k)
                    );
                    setViewColumns(columns);
                }
            } else {
                alert('Error cargando registros: ' + data.message);
            }
        } catch (error) {
            console.error('Error fetching records:', error);
            alert('Error al cargar los registros');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage < 1) return;
        const maxPage = Math.ceil(viewTotal / viewLimit);
        if (newPage > maxPage) return;

        setViewPage(newPage);
        fetchDatasetRecords(viewingDataset, newPage, viewLimit);
    };

    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        setViewLimit(newLimit);
        setViewPage(1); // Resetear a página 1 al cambiar límite
        fetchDatasetRecords(viewingDataset, 1, newLimit);
    };

    // URL de producción
    // URL dinámica
    const API_URL = `${getApiBaseUrl()}/api/data`;

    const handleFileSelect = async (event) => {
        const selectedFile = event.target.files[0];
        if (!selectedFile) return;

        setFile(selectedFile);
        setLoading(true);

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const response = await fetch(`${API_URL}/upload`, {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (data.success) {
                setFileInfo(data);
                setDatasetName(selectedFile.name.split('.')[0]);
                if (data.sheets && data.sheets.length > 0) {
                    setSelectedSheet(data.sheets[0]);
                }
                setStep('preview');
            } else {
                alert('Error: ' + data.message);
            }
        } catch (error) {
            console.error('Error subiendo archivo:', error);
            alert('Error subiendo el archivo');
        } finally {
            setLoading(false);
        }
    };

    const handleImport = async () => {
        if (!datasetName.trim()) {
            alert('Por favor ingrese un nombre para el dataset');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/import`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    file_id: fileInfo.file_id,
                    user_id: userId,
                    dataset_name: datasetName,
                    file_type: fileInfo.file_type,
                    sheet_name: selectedSheet
                })
            });

            const data = await response.json();

            if (data.success) {
                alert(`✅ ${data.records_imported} registros importados exitosamente`);
                setStep('select');
                setFile(null);
                setFileInfo(null);
                loadDatasets();
            } else {
                alert('Error: ' + data.message);
            }
        } catch (error) {
            console.error('Error importando:', error);
            alert('Error importando los datos');
        } finally {
            setLoading(false);
        }
    };

    const loadDatasets = async () => {
        try {
            const response = await fetch(`${API_URL}/datasets?user_id=${userId}`);
            const data = await response.json();
            if (data.success) {
                setDatasets(data.datasets);
            }
        } catch (error) {
            console.error('Error cargando datasets:', error);
        }
    };

    const addManualRow = () => {
        setManualData([...manualData, { nombre: '', email: '', telefono: '' }]);
    };

    const removeManualRow = (index) => {
        const newData = manualData.filter((_, i) => i !== index);
        setManualData(newData);
    };

    const updateManualData = (index, field, value) => {
        const newData = [...manualData];
        newData[index][field] = value;
        setManualData(newData);
    };

    const saveManualData = async () => {
        if (!datasetName.trim()) {
            alert('Por favor ingrese un nombre para el dataset');
            return;
        }

        const validData = manualData.filter(row =>
            row.nombre.trim() || row.email.trim() || row.telefono.trim()
        );

        if (validData.length === 0) {
            alert('Por favor ingrese al menos un registro');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/manual`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: userId,
                    dataset_name: datasetName,
                    records: validData
                })
            });

            const data = await response.json();

            if (data.success) {
                alert(`✅ ${data.records_added} registros guardados exitosamente`);
                setStep('select');
                setManualData([{ nombre: '', email: '', telefono: '' }]);
                setDatasetName('');
                loadDatasets();
            } else {
                alert('Error: ' + data.message);
            }
        } catch (error) {
            console.error('Error guardando datos:', error);
            alert('Error guardando los datos');
        } finally {
            setLoading(false);
        }
    };

    const deleteDataset = async (importId, datasetName) => {
        if (!window.confirm(`¿Está seguro de eliminar el dataset "${datasetName}"?`)) {
            return;
        }

        try {
            const response = await fetch(`${API_URL}/dataset/${importId}?user_id=${userId}`, {
                method: 'DELETE'
            });

            const data = await response.json();

            if (data.success) {
                alert('Dataset eliminado exitosamente');
                loadDatasets();
            } else {
                alert('Error: ' + data.message);
            }
        } catch (error) {
            console.error('Error eliminando dataset:', error);
            alert('Error eliminando el dataset');
        }
    };

    React.useEffect(() => {
        if (step === 'view') {
            loadDatasets();
        }
    }, [step]);

    return (
        <div className="database-manager-overlay">
            <div className="database-manager-modal">
                <div className="db-header">
                    <h2>🗄️ Base de Datos</h2>
                    <button className="db-close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="db-content">
                    {step === 'select' && (
                        <div className="db-step">
                            <h3>¿Cómo desea agregar datos?</h3>
                            <div className="db-options">
                                <button
                                    className="db-option-btn"
                                    onClick={() => fileInputRef.current.click()}
                                >
                                    <span className="db-icon">📁</span>
                                    <span>Importar Archivo</span>
                                    <small>CSV o Excel</small>
                                </button>

                                <button
                                    className="db-option-btn"
                                    onClick={() => setStep('manual')}
                                >
                                    <span className="db-icon">✏️</span>
                                    <span>Capturar Manual</span>
                                    <small>Escribir datos</small>
                                </button>

                                <button
                                    className="db-option-btn"
                                    onClick={() => setStep('view')}
                                >
                                    <span className="db-icon">📊</span>
                                    <span>Ver Mis Datos</span>
                                    <small>Datasets guardados</small>
                                </button>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                        </div>
                    )}

                    {step === 'preview' && fileInfo && (
                        <div className="db-step">
                            <h3>Vista Previa del Archivo</h3>

                            <div className="db-file-info">
                                <p><strong>Archivo:</strong> {fileInfo.file_name}</p>
                                <p><strong>Registros:</strong> {fileInfo.total_records}</p>
                                <p><strong>Columnas:</strong> {fileInfo.columns.length}</p>
                            </div>

                            <div className="db-form-group">
                                <label>Nombre del Dataset:</label>
                                <input
                                    type="text"
                                    value={datasetName}
                                    onChange={(e) => setDatasetName(e.target.value)}
                                    placeholder="Ej: Clientes 2024"
                                />
                            </div>

                            {fileInfo.sheets && fileInfo.sheets.length > 1 && (
                                <div className="db-form-group">
                                    <label>Seleccionar Hoja:</label>
                                    <select
                                        value={selectedSheet}
                                        onChange={(e) => setSelectedSheet(e.target.value)}
                                    >
                                        {fileInfo.sheets.map(sheet => (
                                            <option key={sheet} value={sheet}>{sheet}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="db-preview-table">
                                <h4>Primeras 5 filas:</h4>
                                <div className="table-container">
                                    <table>
                                        <thead>
                                            <tr>
                                                {fileInfo.columns.map(col => (
                                                    <th key={col.name}>{col.name}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fileInfo.preview.map((row, idx) => (
                                                <tr key={idx}>
                                                    {fileInfo.columns.map(col => (
                                                        <td key={col.name}>{row[col.name]}</td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="db-actions">
                                <button
                                    className="db-btn db-btn-secondary"
                                    onClick={() => setStep('select')}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="db-btn db-btn-primary"
                                    onClick={handleImport}
                                    disabled={loading}
                                >
                                    {loading ? 'Importando...' : 'Importar Datos'}
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 'manual' && (
                        <div className="db-step">
                            <h3>Captura Manual de Datos</h3>

                            <div className="db-form-group">
                                <label>Nombre del Dataset:</label>
                                <input
                                    type="text"
                                    value={datasetName}
                                    onChange={(e) => setDatasetName(e.target.value)}
                                    placeholder="Ej: Clientes VIP"
                                />
                            </div>

                            <div className="db-manual-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Nombre</th>
                                            <th>Email</th>
                                            <th>Teléfono</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {manualData.map((row, idx) => (
                                            <tr key={idx}>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={row.nombre}
                                                        onChange={(e) => updateManualData(idx, 'nombre', e.target.value)}
                                                        placeholder="Nombre completo"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="email"
                                                        value={row.email}
                                                        onChange={(e) => updateManualData(idx, 'email', e.target.value)}
                                                        placeholder="email@ejemplo.com"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="tel"
                                                        value={row.telefono}
                                                        onChange={(e) => updateManualData(idx, 'telefono', e.target.value)}
                                                        placeholder="555-1234"
                                                    />
                                                </td>
                                                <td>
                                                    {manualData.length > 1 && (
                                                        <button
                                                            className="db-btn-icon"
                                                            onClick={() => removeManualRow(idx)}
                                                            title="Eliminar fila"
                                                        >
                                                            🗑️
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <button className="db-btn-add" onClick={addManualRow}>
                                    ➕ Agregar Fila
                                </button>
                            </div>

                            <div className="db-actions">
                                <button
                                    className="db-btn db-btn-secondary"
                                    onClick={() => setStep('select')}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="db-btn db-btn-primary"
                                    onClick={saveManualData}
                                    disabled={loading}
                                >
                                    {loading ? 'Guardando...' : 'Guardar Datos'}
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 'view' && (
                        <div className="db-step">
                            <h3>Mis Datasets</h3>

                            {datasets.length === 0 ? (
                                <div className="db-empty">
                                    <p>No tiene datasets guardados</p>
                                    <button
                                        className="db-btn db-btn-primary"
                                        onClick={() => setStep('select')}
                                    >
                                        Agregar Datos
                                    </button>
                                </div>
                            ) : (
                                <div className="db-datasets-list">
                                    {datasets.map(dataset => (
                                        <div key={dataset._id} className="db-dataset-card">
                                            <div className="db-dataset-info">
                                                <h4>{dataset.dataset_name}</h4>
                                                <p>
                                                    <span className="db-badge">{dataset.record_count} registros</span>
                                                    <span className="db-badge">{dataset.file_type}</span>
                                                </p>
                                                <small>
                                                    Importado: {new Date(dataset.imported_at).toLocaleDateString()}
                                                </small>
                                            </div>
                                            <div className="db-dataset-actions">
                                                {onSelect && (
                                                    <button
                                                        className="db-btn db-btn-primary"
                                                        style={{ marginRight: '8px', padding: '4px 12px', fontSize: '0.85rem' }}
                                                        onClick={() => onSelect(dataset)}
                                                    >
                                                        Seleccionar
                                                    </button>
                                                )}
                                                <button
                                                    className="db-btn-icon"
                                                    onClick={() => handleConfigureDataset(dataset)}
                                                    title="Configurar Campos"
                                                    style={{ marginRight: '5px' }}
                                                >
                                                    ⚙️
                                                </button>
                                                <button
                                                    className="db-btn-icon"
                                                    onClick={() => deleteDataset(dataset._id, dataset.dataset_name)}
                                                    title="Eliminar"
                                                >
                                                    🗑️
                                                </button>
                                                <button
                                                    className="db-btn-icon"
                                                    onClick={() => handleViewRecords(dataset)}
                                                    title="Ver Registros"
                                                    style={{ marginLeft: '5px' }}
                                                >
                                                    👁️
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="db-actions">
                                <button
                                    className="db-btn db-btn-secondary"
                                    onClick={() => setStep('select')}
                                >
                                    Volver
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 'configure_fields' && currentConfigDataset && (
                        <div className="db-step">
                            <h3>Configurar Campos: {currentConfigDataset.dataset_name}</h3>
                            <p className="text-sm text-gray-500 mb-4">Define el tipo de dato para cada columna.</p>

                            <div className="db-fields-config-container" style={{ maxHeight: '400px', overflowY: 'auto', padding: '10px', border: '1px solid #eee', borderRadius: '8px' }}>
                                {datasetColumns.map(col => {
                                    const config = fieldConfigs[currentConfigDataset._id]?.[col] || {};
                                    return (
                                        <div key={col} className="db-field-config-row" style={{ marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '6px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                <label style={{ fontWeight: 'bold' }}>{col}</label>
                                                <select
                                                    value={config.type || 'text'}
                                                    onChange={(e) => updateFieldConfig(currentConfigDataset._id, col, 'type', e.target.value)}
                                                    style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ddd' }}
                                                >
                                                    <option value="text">Texto (Default)</option>
                                                    <option value="image_path">Imagen (Ruta Local)</option>
                                                    <option value="image_base64">Imagen (Base64)</option>
                                                    <option value="boolean">Booleano (Sí/No)</option>
                                                </select>
                                            </div>

                                            {config.type === 'image_path' && (
                                                <div style={{ marginTop: '5px' }}>
                                                    <div style={{ marginBottom: '8px' }}>
                                                        <label style={{ fontSize: '0.8rem', display: 'block', marginBottom: '2px' }}>Ruta Base (Directorio):</label>
                                                        <input
                                                            type="text"
                                                            placeholder="Ej: C:/Imagenes/Empleados/"
                                                            value={config.basePath || ''}
                                                            onChange={(e) => updateFieldConfig(currentConfigDataset._id, col, 'basePath', e.target.value)}
                                                            style={{ width: '100%', padding: '6px', fontSize: '0.9rem', border: '1px solid #ddd', borderRadius: '4px' }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label style={{ fontSize: '0.8rem', display: 'block', marginBottom: '2px' }}>Extensión (Opcional):</label>
                                                        <input
                                                            type="text"
                                                            placeholder="Ej: .jpg (dejar vacío si el dato ya la incluye)"
                                                            value={config.suffix || ''}
                                                            onChange={(e) => updateFieldConfig(currentConfigDataset._id, col, 'suffix', e.target.value)}
                                                            style={{ width: '100%', padding: '6px', fontSize: '0.9rem', border: '1px solid #ddd', borderRadius: '4px' }}
                                                        />
                                                    </div>
                                                    <small style={{ color: '#666', fontSize: '0.75rem', display: 'block', marginTop: '4px' }}>
                                                        Resultado: Ruta Base + Valor + Extensión
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="db-actions" style={{ marginTop: '20px' }}>
                                <button
                                    className="db-btn db-btn-secondary"
                                    onClick={() => setStep('view')}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="db-btn db-btn-primary"
                                    onClick={saveFieldConfig}
                                >
                                    Guardar Configuración
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 'view_records' && viewingDataset && (
                        <div className="db-step">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3>Registros: {viewingDataset.dataset_name}</h3>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '0.9rem' }}>Mostrar:</span>
                                    <select
                                        value={viewLimit}
                                        onChange={handleLimitChange}
                                        style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ddd' }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={30}>30</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>

                            <div className="db-preview-table">
                                <div className="table-container" style={{ maxHeight: '400px' }}>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '50px' }}>#</th>
                                                {viewColumns.map(col => (
                                                    <th key={col}>{col}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {viewRecords.map((record, idx) => {
                                                const rowData = record.data || record;
                                                const globalIndex = ((viewPage - 1) * viewLimit) + idx + 1;
                                                return (
                                                    <tr key={idx}>
                                                        <td style={{ color: '#888', fontSize: '0.8rem' }}>{globalIndex}</td>
                                                        {viewColumns.map(col => (
                                                            <td key={col}>{rowData[col]}</td>
                                                        ))}
                                                    </tr>
                                                );
                                            })}
                                            {viewRecords.length === 0 && (
                                                <tr>
                                                    <td colSpan={viewColumns.length + 1} style={{ textAlign: 'center', padding: '20px' }}>
                                                        No hay registros para mostrar
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="db-pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
                                <span style={{ fontSize: '0.9rem', color: '#666' }}>
                                    Mostrando {((viewPage - 1) * viewLimit) + 1} - {Math.min(viewPage * viewLimit, viewTotal)} de {viewTotal} registros
                                </span>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button
                                        className="db-btn db-btn-secondary"
                                        onClick={() => handlePageChange(viewPage - 1)}
                                        disabled={viewPage === 1}
                                        style={{ padding: '4px 12px' }}
                                    >
                                        ◀ Anterior
                                    </button>
                                    <span style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                                        Página {viewPage} de {Math.ceil(viewTotal / viewLimit) || 1}
                                    </span>
                                    <button
                                        className="db-btn db-btn-secondary"
                                        onClick={() => handlePageChange(viewPage + 1)}
                                        disabled={viewPage >= Math.ceil(viewTotal / viewLimit)}
                                        style={{ padding: '4px 12px' }}
                                    >
                                        Siguiente ▶
                                    </button>
                                </div>
                            </div>

                            <div className="db-actions" style={{ marginTop: '20px' }}>
                                <button
                                    className="db-btn db-btn-secondary"
                                    onClick={() => setStep('view')}
                                >
                                    Volver a Datasets
                                </button>
                            </div>
                        </div>
                    )}

                    {loading && (
                        <div className="db-loading-overlay">
                            <div className="db-spinner"></div>
                            <p>Procesando...</p>
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
};

export default DatabaseManager;
